// Benepack OMS · trade api wrapper (D-4.2a · SalesOrder vertical slice)
// SSOT: apps/oms-gateway/internal/controller/trade_handler.go (route 真名 · grep 非 proto)
//
// 范式直接复用 api/master.ts (D-4.1):
//   - list 返 ListWrap<T> ({items, page}) · adapter 直透 resp.data
//   - get/confirm/cancel 返 gateway view struct (PascalCase · 无 json tag → Go field 名)
//   - enum 提交: v7.5.0 gateway protojson 后发 proto 枚举值名 string (enum-codec · master.ts) —
//     但 trade 侧一期无 enum 提交:
//       * SO 无 create/update (SO 由 accepted quote 经 derive-so 派生 · 无手工 Create 端点)
//       * Confirm → 只 path id (无 body)
//       * Cancel  → path id + 可选 reason (StringValue → protojson 裸标量 {reason:"x"} · rc6)
//
// backend 现状 (v7.4.1 · readiness D-4.2 §1 矩阵):
//   GET  /trade/sales-orders                 → List  (SalesOrderInfo[] · 含 page)
//   GET  /trade/sales-orders/{id}            → Get   (SalesOrderDetailView · v7.4.1 含 Lines[])
//   POST /trade/sales-orders/{id}/confirm    → Confirm (draft→confirmed · 返 SalesOrderInfo header)
//   POST /trade/sales-orders/{id}/cancel     → Cancel  (→cancelled · body 可带 reason · 返 header)
//   GET  /trade/sales-orders/{id}/split-suggestions → 只读建议 (D-4.2b 范畴 · 本期不接)
import { http } from './client';
import type {
  QuotationListItem,
  QuotationDetailView,
  SalesOrderListItem,
  SalesOrderDetailView,
  PurchaseOrderListItem,
  PurchaseOrderDetailView,
  ShipmentListItem,
  ShipmentDetailView,
  ReceiveNoteListItem,
  ReceiveNoteDetailView,
  SplitSuggestionLine,
  RecentPurchaseView,
  CreateSoPoLinkResult,
  AggregateView,
  WrapperValue,
  ListWrap,
} from '@/types/views';
import type { AggregateGranularity } from './finance';

// =========== Quotation (D-4.2c-1 · 销售线头) ===========
//
// backend 现状 (readiness D-4.2c-1 §1 矩阵 · grep trade_handler.go 真 route):
//   GET  /trade/quotations                       → List   (QuotationInfo[] · 含 page · 9 字段 header)
//   POST /trade/quotations                       → Create (201 · 返 QuotationInfo header · 不含 lines)
//   GET  /trade/quotations/{id}                  → Get    (QuotationDetailView · 含 Lines[])
//   PUT  /trade/quotations/{id}                  → Update (返 QuotationInfo · P2-7 草稿表头编辑 · 仅 draft · 无 lines/status 字段)
//   POST /trade/quotations/{id}/submit           → Submit (draft→submitted · 返 Info)
//   POST /trade/quotations/{id}/approve          → Approve (submitted→approved · 返 Info · ★ manager/admin perm)
//   POST /trade/quotations/{id}/revise           → Revise (submitted→draft · body reason StringValue · 返 Info)
//   POST /trade/quotations/{id}/send             → Send   (approved→sent · 返 202 AsyncJobAccepted · body to_addrs[] 必填 · SEC-5 空则 422)
//   POST /trade/quotations/{id}/customer-accept  → CustomerAccept (sent→accepted · 终态 · 返 Info · accept 本身不派生 SO)
//   POST /trade/quotations/{id}/derive-so        → DeriveSalesOrder (accepted → 派生 SO · 201 SalesOrderInfo · backend v7.6.0 · sales_order:create)
//   POST /trade/quotations/{id}/customer-reject  → CustomerReject (sent→rejected · body reason · 返 Info)
//   POST /trade/quotations/{id}/cancel           → Cancel  (→cancelled · body reason · 返 Info)
//   POST /trade/quotations/{id}/expire           → Expire  (sent→expired · ★ authorize: system only · 非 UI · 不暴露)
//
// ★ create body 序列化 (proto CreateQuotationRequest · readiness §3 · gateway v7.5.0 protojson):
//   - quotation_no/customer_id/sales_user_id/currency/valid_from/valid_to → raw scalar
//   - tax_rate / line.qty / line.unit_price / line.discount_rate / line.amount
//       → Money/Qty/Percentage custom message · {value:"x"} (protojson 照收 · 同 create-PO)
//   - contact_id (Int64Value) / remark / line.expected_delivery_at (StringValue) → 可选 · 一期省略
//   - 无 enum 字段 (status 由 SM 决定 · create 恒 draft) → 不需 enum-codec
// ★ reason (revise/customer-reject/cancel) 是 StringValue → v7.5.0 protojson 裸标量 {reason:"x"} (同 SO cancel)

export type ListQuotationsResponse = ListWrap<QuotationListItem>;

export interface ListQuotationsRequest {
  page?: number;
  size?: number;
  // 注: gateway ListQuotations handler 一期只取 Page (不解析 status/customer filter · 同 SO/PO)
  status?: string | string[];
  customer_id?: number;
}

/**
 * listQuotations · GET /trade/quotations
 * 返 ListWrap<QuotationListItem> ({items, page}) · 跟 SO/PO/master list 同款 shape。
 */
export async function listQuotations(req: ListQuotationsRequest): Promise<ListQuotationsResponse> {
  const resp = await http.get<ListQuotationsResponse>('/api/v1/trade/quotations', {
    params: { page: req.page, size: req.size },
  });
  return resp.data;
}

/**
 * getQuotation · GET /trade/quotations/{id}
 * 返 QuotationDetailView (header 9 字段平铺 + Lines[]) · 复合页中段 lines 数据源 = 顶层 .Lines。
 */
export async function getQuotation(id: string | number): Promise<QuotationDetailView> {
  const resp = await http.get<QuotationDetailView>(`/api/v1/trade/quotations/${id}`);
  return resp.data;
}

/** CreateQuotationLineInput · 报价行 (proto CreateQuotationLineInput · Money/Qty/Percentage wrapper) */
export interface CreateQuotationLineInput {
  line_no: number;
  product_id: number;
  sku_id: number;
  qty: WrapperValue; // {value:"10.000"}
  unit_price: WrapperValue; // {value:"100.0000"}
  discount_rate: WrapperValue; // {value:"0.0000"} (Percentage)
  amount: WrapperValue; // {value:"1000.0000"} (caller 算 · backend submit 时 recalc)
  // expected_delivery_at / remark 可选 StringValue · 一期省略
}

/** CreateQuotationRequest · 报价单创建 (proto CreateQuotationRequest) */
export interface CreateQuotationRequest {
  quotation_no?: string; // 服务端取号（go-live 反馈 #1 · Q{YYMM}{seq:05d}）· 前端不再传 · 创建后详情/列表显示
  customer_id: number; // cross-schema FK → master.customer
  sales_user_id: number; // cross-schema FK → identity.user (销售归属)
  currency: string; // 3 字符 ISO 4217
  valid_from: string; // "YYYY-MM-DD"
  valid_to: string; // "YYYY-MM-DD"
  tax_rate: WrapperValue; // Percentage {value:"0.1300"}
  lines: CreateQuotationLineInput[]; // 至少 1 行 (backend len==0 报错)
  remark?: string; // 可选 StringValue → protojson 裸标量 (空则省略)
  // contact_id (Int64Value) 可选 · 一期省略
}

/**
 * createQuotation · POST /trade/quotations (201)
 * authorize: quotation:create (sales) · 返 QuotationInfo header (含 ID/QuotationNo · 不含 lines)。
 */
export async function createQuotation(req: CreateQuotationRequest): Promise<QuotationListItem> {
  const resp = await http.post<QuotationListItem>('/api/v1/trade/quotations', req);
  return resp.data;
}

/**
 * UpdateQuotationRequest · 报价单草稿表头编辑 (proto UpdateQuotationRequest · P2-7 UX-7)。
 * ★ 只有表头字段 · proto 无 lines 字段 (M1·5 取舍: Update 不动 line 子表 · backend
 *   usecase 注释) · 无 status 字段 (状态迁移专属状态机端点 · backend 空 status = 保持现状)。
 * quotation_no 服务端取号后不可改 (backend 忽略) · 不传。
 */
export interface UpdateQuotationRequest {
  customer_id: number; // cross-schema FK → master.customer
  contact_id?: number; // Int64Value → protojson 裸数字 (0/省略 = NULL · 编辑时回传原值防丢失)
  sales_user_id: number; // cross-schema FK → identity.user (销售归属 · 编辑时回传原值)
  currency: string; // 3 字符 ISO 4217
  valid_from: string; // "YYYY-MM-DD"
  valid_to: string; // "YYYY-MM-DD"
  tax_rate: WrapperValue; // Percentage {value:"0.1300"}
  remark?: string; // StringValue → 裸标量 (空则省略 → backend 落 NULL)
}

/**
 * updateQuotation · PUT /trade/quotations/{id} (200 · P2-7 草稿编辑)
 * authorize: quotation:update (sales) · ★ 状态守卫 (backend): 仅 draft 可编辑 ·
 *   submitted/approved/sent → 422 BAD_STATE_TRANSITION (须先「退回修订」回 draft) ·
 *   终态 → 422 TERMINAL。返 QuotationInfo header (status 保持 draft)。
 */
export async function updateQuotation(
  id: string | number,
  req: UpdateQuotationRequest,
): Promise<QuotationListItem> {
  const resp = await http.put<QuotationListItem>(`/api/v1/trade/quotations/${id}`, req);
  return resp.data;
}

/**
 * submitQuotation · POST /{id}/submit (draft→submitted)
 * authorize: quotation:submit (sales) · guards: lines_not_empty + dates_valid (backend SM) · 返 Info。
 */
export async function submitQuotation(id: string | number): Promise<QuotationListItem> {
  const resp = await http.post<QuotationListItem>(`/api/v1/trade/quotations/${id}/submit`, {});
  return resp.data;
}

/**
 * approveQuotation · POST /{id}/approve (submitted→approved)
 * ★ authorize: quotation:approve (manager/admin · 非 sales · readiness §4 RBAC) · guard: amount_within_limit。
 */
export async function approveQuotation(id: string | number): Promise<QuotationListItem> {
  const resp = await http.post<QuotationListItem>(`/api/v1/trade/quotations/${id}/approve`, {});
  return resp.data;
}

/**
 * reviseQuotation · POST /{id}/revise (submitted→draft · 退回修订)
 * authorize: quotation:update (sales) · reason 是 StringValue → 裸标量 {reason:"x"} (protojson · 空则省略)。
 */
export async function reviseQuotation(
  id: string | number,
  reason?: string,
): Promise<QuotationListItem> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.post<QuotationListItem>(`/api/v1/trade/quotations/${id}/revise`, body);
  return resp.data;
}

/**
 * sendQuotation · POST /{id}/send (approved→sent · 渲染 PDF + 入队邮件 · 异步)
 * authorize: quotation:send (sales) · 返 202 AsyncJobAccepted (不等真发)。
 * to_addrs 必填 (proto repeated string) · SEC-5: 空则 backend 硬失败 422
 * OMS_TRADE_QUOT_NO_RECIPIENT (曾静默回退 stub 邮箱 · 误投风险已移除) · 调用方先校验非空。
 * ★ 异步: 前端提示「已入队发送」· 不乐观即时刷状态 · 用户手动刷新看 status 转 sent。
 */
export async function sendQuotation(
  id: string | number,
  toAddrs?: string[],
): Promise<AsyncJobAccepted> {
  const body = toAddrs && toAddrs.length ? { to_addrs: toAddrs } : {};
  const resp = await http.post<AsyncJobAccepted>(`/api/v1/trade/quotations/${id}/send`, body);
  return resp.data;
}

/**
 * customerAcceptQuotation · POST /{id}/customer-accept (sent→accepted · 终态)
 * authorize: quotation:update (sales) · 返 QuotationInfo (status=accepted)。
 * ⛔ readiness D-4.2c-1 §6: 此操作【不派生 SO】· accepted 是终态 · SO 派生 surface 待 backend patch。
 */
export async function customerAcceptQuotation(id: string | number): Promise<QuotationListItem> {
  const resp = await http.post<QuotationListItem>(
    `/api/v1/trade/quotations/${id}/customer-accept`,
    {},
  );
  return resp.data;
}

/**
 * customerRejectQuotation · POST /{id}/customer-reject (sent→rejected · 终态)
 * authorize: quotation:update (sales) · reason StringValue 裸标量 {reason:"x"} (空则省略) · 返 Info。
 */
export async function customerRejectQuotation(
  id: string | number,
  reason?: string,
): Promise<QuotationListItem> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.post<QuotationListItem>(
    `/api/v1/trade/quotations/${id}/customer-reject`,
    body,
  );
  return resp.data;
}

/**
 * cancelQuotation · POST /{id}/cancel (→cancelled · 终态)
 * authorize: quotation:cancel (sales/admin) · reason StringValue 裸标量 {reason:"x"} (空则省略) · 返 Info。
 */
export async function cancelQuotation(
  id: string | number,
  reason?: string,
): Promise<QuotationListItem> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.post<QuotationListItem>(`/api/v1/trade/quotations/${id}/cancel`, body);
  return resp.data;
}

/**
 * deriveSalesOrder · POST /{id}/derive-so (accepted quotation → 派生 SO · 201)
 * 销售线头尾闭合 (B1 · backend v7.6.0) · authorize: sales_order:create
 *   (下游 trade gRPC RBACInterceptor · sales 角色 · gateway 透传 Authorization metadata)。
 * 返 SalesOrderInfo header (PascalCase · 含 .ID/.SoNo · 同 SalesOrderListItem) → 用 .ID 跳 SO detail。
 * 业务错: quotation 非 accepted → 422 · 同 quotation 已派生 → 409 CONFLICT (usecase list-first 查重)。
 * body 可选 (仅承载 idempotency_key · v7.7 接 idempotency_record 后启用) → 一期传 {}。
 */
export async function deriveSalesOrder(quotationId: string | number): Promise<SalesOrderListItem> {
  const resp = await http.post<SalesOrderListItem>(
    `/api/v1/trade/quotations/${quotationId}/derive-so`,
    {},
  );
  return resp.data;
}

/** CreateSalesOrderLineInput · 手工直建 SO 的行 (proto CreateSalesOrderLineInput) */
export interface CreateSalesOrderLineInput {
  line_no: number;
  product_id: number;
  sku_id: number;
  qty: WrapperValue; // {value:"10.000"}
  unit_price: WrapperValue; // {value:"100.0000"}
  amount: WrapperValue; // {value:"1000.0000"} · 服务端 H2 重算 (不信客户端)
  // expected_delivery_at / remark 可选 StringValue · 省略 if 空
}

/** CreateSalesOrderRequest · 手工直建 SO 请求 (proto CreateSalesOrderRequest · WP-灵活入口) */
export interface CreateSalesOrderRequest {
  customer_id: number; // cross-schema FK → master.customer · 必 active (backend 校验)
  contact_id?: number; // Int64Value · 省略 if 0
  // sales_user_id 不传：省略时下游 trade handler 注入调用者 (actor.UserID · 防裸 FK + X3 收窄)
  currency: string; // 3 字符 ISO 4217 (backend 校验 len==3)
  customer_po_no?: string; // StringValue · 客户方 PO 号 · 省略 if 空
  tax_rate?: WrapperValue; // Percentage {value:"0.1300"} · 省略则 backend 默认 0.1300
  lines: CreateSalesOrderLineInput[]; // 至少 1 行 (backend OMS_TRADE_SO_INVALID_LINES)
  // source 服务端固定 manual (本请求无 source 字段)
}

/**
 * createSalesOrder · POST /trade/sales-orders (手工直建 SO · WP-灵活入口 · 201)
 * authorize: sales_order:create (sales · 下游 trade gRPC RBACInterceptor)。
 * 返 SalesOrderInfo header (含 ID/SoNo · 不含 lines) → 取 so_line id 需再 getSalesOrder(ID)。
 * 与报价派生 deriveSalesOrder 并存：本入口跳过报价单，source=manual。
 */
export async function createSalesOrder(
  req: CreateSalesOrderRequest,
): Promise<SalesOrderListItem> {
  const resp = await http.post<SalesOrderListItem>('/api/v1/trade/sales-orders', req);
  return resp.data;
}

/**
 * openPdfBlob · 把 PDF blob 用带 Bearer 的响应字节 → URL.createObjectURL → window.open 新标签打开。
 * 预览不落库治理 (2026-07) 共用 (与 platform openDocumentContent 同款范式 · 但走 POST 直取渲染字节)。
 */
function openPdfBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  // 给新标签足够时间加载 blob 后再回收 objectURL (过早 revoke 会导致空白页)。
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * previewQuotationPdf · POST /trade/quotations/{id}/preview-pdf (GL-41 · 按需渲染当前态)
 * authorize: quotation:read · 空 body · 预览不落库治理 (2026-07)：端点【一跳直流】回 **PDF 字节**
 * (Content-Type: application/pdf) → 带 Bearer 拿 blob → window.open 新标签 (内部完成 · 返 void)。
 *
 * ★ 任意阶段可调 (draft/submitted/approved/sent · 后端无状态门 · 草稿缺字段渲染空白不报错)。
 * ★ 不再产 document 行 / doc_id (消除孤儿预览档累积)·不再是两跳 (预览→DocID→openDocumentContent)。
 */
export async function previewQuotationPdf(id: string | number): Promise<void> {
  const resp = await http.post<Blob>(
    `/api/v1/trade/quotations/${id}/preview-pdf`,
    {},
    { responseType: 'blob' },
  );
  openPdfBlob(resp.data);
}

// =========== SalesOrder ===========

export interface ListSalesOrdersRequest {
  page?: number;
  size?: number;
  // 注: gateway ListSalesOrders handler 一期只取 Page (不解析 status/customer filter · readiness §1)
  //     下列 filter 字段留 type 占位 · 实际不发 (避免误导) · backend 微迭代补 filter 后启用
  status?: string | string[];
  customer_id?: number;
  sort?: string;
}

/**
 * ListSalesOrdersResponse · gateway 真返 shape
 * SSOT: apps/oms-gateway/internal/trade_client/mapper.go:SalesOrderListView ({items, page})
 */
export type ListSalesOrdersResponse = ListWrap<SalesOrderListItem>;

export async function listSalesOrders(
  req: ListSalesOrdersRequest,
): Promise<ListSalesOrdersResponse> {
  // 只发 page/size (gateway 一期未解析其余 filter)
  const resp = await http.get<ListSalesOrdersResponse>('/api/v1/trade/sales-orders', {
    params: { page: req.page, size: req.size },
  });
  return resp.data;
}

/**
 * getSalesOrder · GET /trade/sales-orders/{id}
 * 返 SalesOrderDetailView (header 9 字段平铺 + Lines[] · v7.4.1 detail patch)
 * 复合页中段 lines ProTable 数据源 = 返回顶层 .Lines 数组 (embed 平铺 · 非嵌套 key)
 */
export async function getSalesOrder(id: string | number): Promise<SalesOrderDetailView> {
  const resp = await http.get<SalesOrderDetailView>(`/api/v1/trade/sales-orders/${id}`);
  return resp.data;
}

/**
 * confirmSalesOrder · POST /trade/sales-orders/{id}/confirm (draft→confirmed)
 * authorize: sales_order:confirm · guards: lines_valid + lines_priced (backend SM)
 * 返 SalesOrderInfo header (= SalesOrderListItem shape · 不含 lines)
 */
export async function confirmSalesOrder(id: string | number): Promise<SalesOrderListItem> {
  const resp = await http.post<SalesOrderListItem>(`/api/v1/trade/sales-orders/${id}/confirm`, {});
  return resp.data;
}

/**
 * cancelSalesOrder · POST /trade/sales-orders/{id}/cancel (→cancelled)
 * authorize: sales_order:cancel · guard: no_po_sent (backend SM)
 *
 * reason 是 proto google.protobuf.StringValue (well-known wrapper) ·
 *   v7.5.0 gateway DecodeJSON 换 protojson · well-known wrapper 期望【裸标量】 →
 *   wire 形态 { reason: "..." } (不再包 {value:x} · protojson 拒 object 包裹)。
 *   ★ rc6 改点 (v7.5.0 protojson 联动): {reason:{value:x}} → {reason:"x"}。
 *      空 reason 直接省略 (字段 optional · 省略合法)。
 * 返 SalesOrderInfo header。
 */
export async function cancelSalesOrder(
  id: string | number,
  reason?: string,
): Promise<SalesOrderListItem> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.post<SalesOrderListItem>(`/api/v1/trade/sales-orders/${id}/cancel`, body);
  return resp.data;
}

/**
 * attachSoCustomerPdf · POST /trade/sales-orders/{id}/customer-pdf (GL-42)
 * 把已上传的客户 PDF (platform.document) 关联到 SO。
 *
 * 两步：先 api/platform.uploadDocument(file) 拿 DocID，再调本函数 attach (soId, docId)。
 * authorize: sales_order:update · 任意非终态可附 (backend usecase 校验 + DB FK 强制 doc 存在)。
 * 返 SalesOrderInfo header (= SalesOrderListItem · 含 CustomerPdfDocID·刷新详情用)。
 */
export async function attachSoCustomerPdf(
  id: string | number,
  docId: number,
): Promise<SalesOrderListItem> {
  const resp = await http.post<SalesOrderListItem>(
    `/api/v1/trade/sales-orders/${id}/customer-pdf`,
    { doc_id: docId },
  );
  return resp.data;
}

// =========== SalesOrder 拆单写链 (D-4.2b-1 · v7.4.4 surface) ===========
//
// backend 现状 (readiness D-4.2b §2 + memory oms_v7_4_4_so_split_writechain_bridge):
//   POST /trade/sales-orders/{id}/start-split → confirmed→splitting · 空 body · 返 SalesOrderInfo
//   POST /trade/sales-orders/{id}/split-done  → splitting→po_issued · guard all_lines_split · 空 body · 返 Info
//   GET  /trade/sales-orders/{id}/split-suggestions → 只读建议 ({items:[{so_line_id, suggestions[]}]})
//
// RBAC: 拆单链全 procurement 角色 (sales_order:split · 任务书写 sales 有误 · memory 已纠偏)。

/** proto raw 透传的单条建议 (encoding/json on tradev1.SplitSuggestion_Suggestion · snake_case) */
interface RawSplitSuggestion {
  supplier_id?: number;
  supplier_name?: string;
  suggested_qty?: WrapperValue | null; // {value:"..."} (Qty wrapper · 可空)
  recent_unit_price?: WrapperValue | null; // {value:"..."} (Money wrapper · 可空)
  avg_lead_time_days?: number;
  rationale?: string;
}
interface RawSplitSuggestionLine {
  so_line_id?: number;
  suggestions?: RawSplitSuggestion[] | null;
}
interface RawSplitSuggestionsResponse {
  items?: RawSplitSuggestionLine[] | null;
}

/**
 * getSplitSuggestions · GET /trade/sales-orders/{id}/split-suggestions (只读建议)
 * gateway 真返 {items:[{so_line_id, suggestions:[{supplier_id, supplier_name,
 *   suggested_qty:{value}, recent_unit_price:{value}, avg_lead_time_days, rationale}]}]}
 *   (items lowercase tag · suggestions 是 proto raw 透传 · snake_case + Qty/Money {value} wrapper)
 * → adapter 拍平: 解包 {value} · camelCase · 供拆单页只读展示。
 */
export async function getSplitSuggestions(id: string | number): Promise<SplitSuggestionLine[]> {
  const resp = await http.get<RawSplitSuggestionsResponse>(
    `/api/v1/trade/sales-orders/${id}/split-suggestions`,
  );
  const items = resp.data?.items ?? [];
  return items.map((line) => ({
    soLineId: line.so_line_id ?? 0,
    suggestions: (line.suggestions ?? []).map((s) => ({
      supplierId: s.supplier_id ?? 0,
      supplierName: s.supplier_name ?? '',
      suggestedQty: s.suggested_qty?.value ?? '',
      recentUnitPrice: s.recent_unit_price?.value ?? '',
      avgLeadTimeDays: s.avg_lead_time_days ?? 0,
      rationale: s.rationale ?? '',
    })),
  }));
}

/**
 * startSplitSalesOrder · POST /{id}/start-split (confirmed→splitting)
 * authorize: sales_order:split (procurement) · 空 body · 返 SalesOrderInfo header (Status="splitting")
 */
export async function startSplitSalesOrder(id: string | number): Promise<SalesOrderListItem> {
  const resp = await http.post<SalesOrderListItem>(
    `/api/v1/trade/sales-orders/${id}/start-split`,
    {},
  );
  return resp.data;
}

/**
 * splitDoneSalesOrder · POST /{id}/split-done (splitting→po_issued)
 * authorize: sales_order:split (procurement) · guard all_lines_split (qty_split==qty 全行) ·
 *   空 body · 返 SalesOrderInfo header (Status="po_issued")。guard 未过时 backend 返业务错 (axios toast)。
 */
export async function splitDoneSalesOrder(id: string | number): Promise<SalesOrderListItem> {
  const resp = await http.post<SalesOrderListItem>(
    `/api/v1/trade/sales-orders/${id}/split-done`,
    {},
  );
  return resp.data;
}

// =========== PurchaseOrder create + SoPoLink (D-4.2b-1 拆单生成) ===========
//
// ★ create-PO / so-po-link body 真类型 (proto trade.proto:407/452 + e2e-split-chain-v744.sh 真 body):
//   gateway DecodeJSON (v7.5.0 起 protojson) 绑 proto message · Money/Qty/Percentage 是 proto custom
//   message → wire {value:"..."} (非 raw · 跟 master raw scalar 相反 · 同 CreateQuotation · protojson 不变)。
//   enum 无 (currency 是 string)。可选字段 (contact_id/expected_arrival/ship_method/remark) protojson
//   后可传 · 一期仍省略 (拆单链必填字段已足 · 透传扩展属 backlog · 非 WP-06 序列化归一范畴)。

/** CreatePurchaseOrderLineInput · 拆单生成 PO 的行 (proto CreatePurchaseOrderLineInput) */
export interface CreatePurchaseOrderLineInput {
  line_no: number;
  product_id: number;
  sku_id: number;
  qty: WrapperValue; // {value:"100.000"}
  unit_price: WrapperValue; // {value:"12.5000"}
  amount: WrapperValue; // {value:"1250.0000"}
  // expected_arrival_at / remark 可选 StringValue · 一期省略 (gateway 标准 json)
}

/** CreatePurchaseOrderRequest · 拆单生成 PO 请求 (proto CreatePurchaseOrderRequest) */
export interface CreatePurchaseOrderRequest {
  po_no?: string; // 服务端取号（go-live 反馈 #1 · PO{YYMM}{seq:05d}）· 前端不再传 · 创建后详情/列表显示
  supplier_id: number; // cross-schema FK → master.supplier · 必 active (backend 校验)
  buyer_user_id: number; // cross-schema FK → identity.user
  currency: string; // 3 字符 ISO 4217 (backend 校验 len==3)
  tax_rate?: WrapperValue; // Percentage {value:"0.1300"} · 省略则 backend 默认 0.1300
  lines: CreatePurchaseOrderLineInput[]; // 至少 1 行 (backend OMS_TRADE_PO_INVALID_LINES)
}

/**
 * createPurchaseOrder · POST /trade/purchase-orders (拆单链②生成 PO · 201)
 * authorize: purchase_order:create (procurement) · 返 PurchaseOrderInfo header (含 ID/PoNo · 不含 lines)
 *   → 取 po_line id 需再 getPurchaseOrder(ID) 拿 Lines[] (create 返 header-only)。
 */
export async function createPurchaseOrder(
  req: CreatePurchaseOrderRequest,
): Promise<PurchaseOrderListItem> {
  const resp = await http.post<PurchaseOrderListItem>('/api/v1/trade/purchase-orders', req);
  return resp.data;
}

/** CreateSoPoLinkRequest · SO 行↔PO 行关联 (proto CreateSoPoLinkRequest) */
export interface CreateSoPoLinkRequest {
  so_line_id: number;
  po_line_id: number;
  qty: WrapperValue; // {value:"100.000"} · 本次关联承担数量 (backend ck qty>0 + over-split usecase 校验)
  // remark 可选 StringValue · 一期省略
}

/**
 * createSoPoLink · POST /trade/so-po-links (拆单链③ SO 行↔PO 行关联 · 201)
 * authorize: so_po_link:create (procurement) · 事务内累加 so_line.qty_split + over-split 校验。
 * 返 CreateSoPoLinkView {Link, SoLineQtySplit} · SoLineQtySplit = 累加后已拆量 (进度即时展示)。
 */
export async function createSoPoLink(req: CreateSoPoLinkRequest): Promise<CreateSoPoLinkResult> {
  const resp = await http.post<CreateSoPoLinkResult>('/api/v1/trade/so-po-links', req);
  return resp.data;
}

/**
 * getRecentPurchase · GET /trade/purchase-orders/recent-purchase (perm purchase_order:read)
 *
 * P2-2 拆单增强 (PRD-4+UX-3·旧 X20)：按 (supplier, sku) 查最近成交单价 + 最近交期，
 * 拆单页生成 PO 时的参考价 (可编辑单价的默认建议 · 跟 master getPriceQuote P2-1 同范式)。
 * ★ _silentError: 参考价是背景增强 · 无历史返 Found=false (200 非错) · 网络/权限错也
 *   不弹全局红 toast · 调用方 catch 后静默降级 (不填不报错)。
 */
export async function getRecentPurchase(params: {
  supplier_id: number;
  sku_id: number;
}): Promise<RecentPurchaseView> {
  const resp = await http.get<RecentPurchaseView>('/api/v1/trade/purchase-orders/recent-purchase', {
    params,
    _silentError: true,
  });
  return resp.data;
}

// =========== PurchaseOrder (D-4.2b-2) ===========
//
// backend 现状 (readiness D-4.2b §1 矩阵 · grep trade_handler.go 真 route):
//   GET  /trade/purchase-orders                     → List (PurchaseOrderInfo[] · 含 page · 8 字段 header)
//   GET  /trade/purchase-orders/{id}                → Get  (PurchaseOrderDetailView · v7.4.1 含 Lines[])
//   POST /trade/purchase-orders/{id}/submit-approval → SubmitApproval (draft→pending_approval · 返 Info)
//   POST /trade/purchase-orders/{id}/send            → Send   (approved→pending_send · 返 202 AsyncJobAccepted)
//   POST /trade/purchase-orders/{id}/resend          → Resend (重发邮件 · 不改主状态 · 返 202 AsyncJobAccepted)
//   POST /trade/purchase-orders/{id}/mark-acknowledged → MarkAcknowledged (sent→acknowledged · 返 Info)
//   POST /trade/purchase-orders/{id}/cancel          → Cancel (→cancelled · body reason StringValue · 返 Info)
//
// action body 真类型 (api/proto/trade/v1/trade.proto:385-398 · 已 grep 确认):
//   - SubmitApproval / Resend : 仅 path id (gateway handler 不 DecodeJSON) → 发空 body {}
//   - Send                    : 所有字段可选 (template_code/contact_id_override/cc_emails) → 一期发空 body {}
//   - MarkAcknowledged        : 所有字段可选 (acknowledged_at/remark) → 一期发空 body {} (backend 用 now())
//   - Cancel                  : reason 是 google.protobuf.StringValue → v7.5.0 protojson 裸标量 {reason:"x"} (同 SO cancel)
//
// v7.4.3 已修 trade→master/platform 二级 auth forward · submit-approval 触发 trade→platform 审批流不再 401。

export type ListPurchaseOrdersResponse = ListWrap<PurchaseOrderListItem>;

export interface ListPurchaseOrdersRequest {
  page?: number;
  size?: number;
  // 注: gateway ListPurchaseOrders handler 一期只取 Page (不解析 supplier_id/status filter · readiness §3.1)
  //     → 本 adapter 只发 page/size · filter 字段留 type 占位 · backend 补 filter 后启用 (status 走 proto 枚举值名)
  supplier_id?: number;
  status?: string | string[];
}

/**
 * listPurchaseOrders · GET /trade/purchase-orders
 * 返 ListWrap<PurchaseOrderListItem> ({items, page}) · 跟 SO/master list 同款 shape。
 */
export async function listPurchaseOrders(
  req: ListPurchaseOrdersRequest,
): Promise<ListPurchaseOrdersResponse> {
  const resp = await http.get<ListPurchaseOrdersResponse>('/api/v1/trade/purchase-orders', {
    params: { page: req.page, size: req.size },
  });
  return resp.data;
}

/**
 * getPurchaseOrder · GET /trade/purchase-orders/{id}
 * 返 PurchaseOrderDetailView (header 8 字段平铺 + Lines[] · v7.4.1 detail patch)
 * 复合页中段 lines ProTable 数据源 = 返回顶层 .Lines 数组 (embed 平铺 · 非嵌套 key)
 */
export async function getPurchaseOrder(id: string | number): Promise<PurchaseOrderDetailView> {
  const resp = await http.get<PurchaseOrderDetailView>(`/api/v1/trade/purchase-orders/${id}`);
  return resp.data;
}

/**
 * listDerivedPurchaseOrders · GET /trade/sales-orders/{id}/derived-pos (P2-4 · UX-6)
 * 按 SO 反查派生 PO (so_po_link 行级关联后端上卷 · 全量不分页 · 一单派生 PO 通常个位数)。
 * authorize: purchase_order:read (下游 trade RBACInterceptor · X3 按 buyer_user_id 收窄)。
 * 返 ListWrap<PurchaseOrderListItem> (与 GET /purchase-orders 同 shape) · SO 详情关联单据卡消费。
 */
export async function listDerivedPurchaseOrders(
  soId: string | number,
): Promise<ListPurchaseOrdersResponse> {
  const resp = await http.get<ListPurchaseOrdersResponse>(
    `/api/v1/trade/sales-orders/${soId}/derived-pos`,
  );
  return resp.data;
}

/**
 * listSourceSalesOrders · GET /trade/purchase-orders/{id}/source-sos (P2-4 · UX-6)
 * 按 PO 反查来源 SO (listDerivedPurchaseOrders 镜像方向)。
 * authorize: sales_order:read (X3 按 sales_user_id 收窄)。
 * 返 ListWrap<SalesOrderListItem> · PO 详情关联单据卡消费。
 */
export async function listSourceSalesOrders(
  poId: string | number,
): Promise<ListSalesOrdersResponse> {
  const resp = await http.get<ListSalesOrdersResponse>(
    `/api/v1/trade/purchase-orders/${poId}/source-sos`,
  );
  return resp.data;
}

/**
 * submitPurchaseOrderApproval · POST /{id}/submit-approval (draft→pending_approval)
 * authorize: purchase_order:submit_approval (procurement) · 触发 trade→platform 审批流 (v7.4.3 auth forward)
 * 返 PurchaseOrderInfo header (= PurchaseOrderListItem shape · 不含 lines)
 */
export async function submitPurchaseOrderApproval(
  id: string | number,
): Promise<PurchaseOrderListItem> {
  const resp = await http.post<PurchaseOrderListItem>(
    `/api/v1/trade/purchase-orders/${id}/submit-approval`,
    {},
  );
  return resp.data;
}

/** AsyncJobAccepted · send/resend 返 (异步 job · 非 PO 实体 · HTTP 202) */
export interface AsyncJobAccepted {
  job_id: number; // snake_case JSON tag (ProtoToAsyncJobView)
  kind: string; // "email" | "pdf_render" | "approval"
}

/**
 * sendPurchaseOrder · POST /{id}/send (approved→pending_send · 异步渲染+发邮件)
 * authorize: purchase_order:send_email (procurement) · 返 202 AsyncJobAccepted (不等真发)
 * ★ 异步: 前端提示「已入队发送」· 不乐观即时刷状态 · 用户手动刷新看 status 转 sent
 * ★ contactIdOverride (loose-ends ②): 发送时选的收件联系人 → body { contact_id_override }
 *   (proto Int64Value · protojson 收裸数字) · backend 在 guard 前覆盖 PO contact_id 并持久化;
 *   省略 → 空 body · 沿用 PO 既有 contact_id (无则 422 OMS_TRADE_PO_NO_CONTACT_EMAIL)。
 *   template_code/cc_emails 仍走 backend 默认。
 */
export async function sendPurchaseOrder(
  id: string | number,
  contactIdOverride?: number,
): Promise<AsyncJobAccepted> {
  const body =
    contactIdOverride && contactIdOverride > 0 ? { contact_id_override: contactIdOverride } : {};
  const resp = await http.post<AsyncJobAccepted>(`/api/v1/trade/purchase-orders/${id}/send`, body);
  return resp.data;
}

/**
 * resendPurchaseOrder · POST /{id}/resend (重发邮件 · 不改主状态)
 * authorize: purchase_order:resend_email (procurement/admin) · 返 202 AsyncJobAccepted
 * ★ 异步: 同 send · 提示「已入队重发」
 */
export async function resendPurchaseOrder(id: string | number): Promise<AsyncJobAccepted> {
  const resp = await http.post<AsyncJobAccepted>(`/api/v1/trade/purchase-orders/${id}/resend`, {});
  return resp.data;
}

/**
 * markPoAcknowledged · POST /{id}/mark-acknowledged (sent→acknowledged · 工厂确认接单)
 * authorize: purchase_order:mark_acknowledged (procurement) · 返 PurchaseOrderInfo header
 * 一期发空 body (acknowledged_at/remark 可选 · backend 用 now())
 */
export async function markPoAcknowledged(id: string | number): Promise<PurchaseOrderListItem> {
  const resp = await http.post<PurchaseOrderListItem>(
    `/api/v1/trade/purchase-orders/${id}/mark-acknowledged`,
    {},
  );
  return resp.data;
}

/**
 * cancelPurchaseOrder · POST /{id}/cancel (→cancelled · guard not_received_yet)
 * authorize: purchase_order:cancel (procurement/admin)
 *
 * reason 是 proto google.protobuf.StringValue (well-known wrapper) · 同 SO cancel ·
 *   v7.5.0 gateway DecodeJSON 换 protojson · well-known wrapper 期望【裸标量】 →
 *   wire 形态 { reason: "..." } (不再包 {value:x})。
 *   ★ rc6 改点 (v7.5.0 protojson 联动)。空 reason 直接省略 (optional)。
 * 返 PurchaseOrderInfo header。
 */
export async function cancelPurchaseOrder(
  id: string | number,
  reason?: string,
): Promise<PurchaseOrderListItem> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.post<PurchaseOrderListItem>(
    `/api/v1/trade/purchase-orders/${id}/cancel`,
    body,
  );
  return resp.data;
}

/**
 * previewPurchaseOrderPdf · POST /trade/purchase-orders/{id}/preview-pdf (GL-41 · 按需渲染当前态)
 * authorize: purchase_order:read · 空 body · 预览不落库治理 (2026-07)：端点【一跳直流】回 **PDF 字节**
 * → 带 Bearer 拿 blob → window.open 新标签 (内部完成 · 返 void)。
 *
 * ★ 任意阶段可调 (draft/in_approval/approved/sent/... · 后端无状态门 · 草稿缺字段渲染空白不报错)。
 * ★ 与 GL-15a 「查看 PDF」(基于 PdfDocID·send 后回写的官方存档 · 走 openDocumentContent) 语义区分:
 *     本端点 = 按需即时渲染当前数据 (任意阶段) · 不落库 (不产 doc_id)。
 */
export async function previewPurchaseOrderPdf(id: string | number): Promise<void> {
  const resp = await http.post<Blob>(
    `/api/v1/trade/purchase-orders/${id}/preview-pdf`,
    {},
    { responseType: 'blob' },
  );
  openPdfBlob(resp.data);
}

// =========== Shipment (WP-07 · v0.1-frontend-rc9 · 履约发货线) ===========
//
// backend 现状 (gateway v7.7.0 · WP-05 桥接 · grep trade_handler.go:90-102 真 route):
//   GET   /trade/shipments               → List     (ShipmentInfo[] · 含 page)
//   POST  /trade/shipments               → Create   (201 · ShipmentDetailView · 本期前端不做手工新建 · 见下)
//   GET   /trade/shipments/{id}          → Get      (ShipmentDetailView · header 平铺 + Lines[])
//   PATCH /trade/shipments/{id}/dispatch → Dispatch (pending→in_transit · 返 ShipmentDetailView)
//   PATCH /trade/shipments/{id}/sign     → Sign     (in_transit→delivered · 返 ShipmentDetailView)
//   PATCH /trade/shipments/{id}/cancel   → Cancel   (→cancelled · body 可带 reason · 返 ShipmentDetailView)
//
// ★ 动作走 PATCH (state transition 语义 · gateway WP-05 选 PATCH · 区别于 quotation/SO/PO 的 POST) →
//   用 http.patch (client.ts rc9 补) · 与 trade REST router.go:135-137 一致。
// ★ 动作返 ShipmentDetailView (含 Lines · gateway WriteJSON(ProtoToShipmentDetailView)) · 非仅 header。
// ★ cancel reason 是 proto StringValue → v7.5.0 protojson 裸标量 {reason:"x"} (同 PO/SO cancel · 空则省略)。
// ★ 手工 Create：gateway 已透出 POST /trade/shipments (CreateShipmentRequest) · 但 M-B 北极星流
//   发货单宜从 SO 派生 (create form 留后续 WP) · 本期前端只做读 + 状态机驱动 (list/detail)。

export type ListShipmentsResponse = ListWrap<ShipmentListItem>;

export interface ListShipmentsRequest {
  page?: number;
  size?: number;
  // P2-4 (UX-5/6): gateway 已解析 ?so_id= (按 SO 反查发货单 · trade List filter 早已支持 ·
  //   缺口只在 gateway query 透传 · 本次补齐) · status filter 仍未接线 (留 type 占位)。
  so_id?: number;
  status?: string | string[];
}

/**
 * listShipments · GET /trade/shipments
 * 返 ListWrap<ShipmentListItem> ({items, page}) · 跟 SO/PO list 同款 shape。
 * so_id (P2-4): 按 SO 反查发货单 (SO 详情关联单据卡消费) · >0 才发。
 */
export async function listShipments(req: ListShipmentsRequest): Promise<ListShipmentsResponse> {
  const resp = await http.get<ListShipmentsResponse>('/api/v1/trade/shipments', {
    params: {
      page: req.page,
      size: req.size,
      ...(req.so_id && req.so_id > 0 ? { so_id: req.so_id } : {}),
    },
  });
  return resp.data;
}

/**
 * getShipment · GET /trade/shipments/{id}
 * 返 ShipmentDetailView (header 12 字段平铺 + Lines[] · embed 平铺非嵌套 key)。
 */
export async function getShipment(id: string | number): Promise<ShipmentDetailView> {
  const resp = await http.get<ShipmentDetailView>(`/api/v1/trade/shipments/${id}`);
  return resp.data;
}

/**
 * dispatchShipment · PATCH /{id}/dispatch (pending→in_transit · 标记发运)
 * authorize: shipment:update (qc 角色) · guard: lines_have_batch + tracking_present_if_carrier_set
 * 返 ShipmentDetailView (含 shipped_at 已置)。
 */
export async function dispatchShipment(id: string | number): Promise<ShipmentDetailView> {
  const resp = await http.patch<ShipmentDetailView>(`/api/v1/trade/shipments/${id}/dispatch`, {});
  return resp.data;
}

/**
 * signShipment · PATCH /{id}/sign (in_transit→delivered · 客户签收 · 终态)
 * authorize: shipment:update (qc 角色) · 返 ShipmentDetailView (含 delivered_at 已置)。
 */
export async function signShipment(id: string | number): Promise<ShipmentDetailView> {
  const resp = await http.patch<ShipmentDetailView>(`/api/v1/trade/shipments/${id}/sign`, {});
  return resp.data;
}

/**
 * cancelShipment · PATCH /{id}/cancel (pending/in_transit→cancelled · guard not_signed_yet)
 * authorize: shipment:cancel · reason 是 proto StringValue → 裸标量 {reason:"x"} (空则省略)。
 * 返 ShipmentDetailView。
 */
export async function cancelShipment(
  id: string | number,
  reason?: string,
): Promise<ShipmentDetailView> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.patch<ShipmentDetailView>(`/api/v1/trade/shipments/${id}/cancel`, body);
  return resp.data;
}

/** CreateShipmentLineInput · 发货行 (proto CreateShipmentLineInput · Qty {value} · batch_id NOT NULL) */
export interface CreateShipmentLineInput {
  line_no: number;
  so_line_id: number;
  batch_id: number;
  qty: WrapperValue; // {value:"100.000"}
  remark?: string; // StringValue 裸标量 · 省略 if 空
}

/** CreateShipmentRequest · 发货单创建 (proto CreateShipmentRequest · WP-05 v7.7.0 · create-form 收口) */
export interface CreateShipmentRequest {
  dn_no?: string; // 服务端取号（go-live 反馈 #1 · DN{YYMM}{seq:05d}）· 前端不再传 · 创建后详情/列表显示
  so_id: number;
  ship_to_addr_id?: number; // Int64Value 裸 number · 省略 if 0
  carrier?: string; // StringValue 裸标量
  tracking_no?: string;
  ship_method?: string;
  remark?: string;
  lines: CreateShipmentLineInput[];
}

/**
 * createShipment · POST /trade/shipments (201)
 * authorize: shipment:create (qc) · 返 ShipmentDetailView (含 Lines · 初态 pending)。
 */
export async function createShipment(req: CreateShipmentRequest): Promise<ShipmentDetailView> {
  const resp = await http.post<ShipmentDetailView>('/api/v1/trade/shipments', req);
  return resp.data;
}

// =========== ReceiveNote (WP-08 · v0.1-frontend-rc10 · 履约收货线) ===========
//
// backend 现状 (gateway v7.7.0 · WP-05 桥接 · grep trade_handler.go:104-112 真 route):
//   GET   /trade/receive-notes                 → List      (ReceiveNoteInfo[] · 含 page)
//   POST  /trade/receive-notes                 → Create    (201 · ReceiveNoteDetailView · 本期前端不做手工新建)
//   GET   /trade/receive-notes/{id}            → Get       (ReceiveNoteDetailView · header 平铺 + Lines[])
//   PATCH /trade/receive-notes/{id}/qc-pass    → QCPass    (pending/partial→passed · 返 DetailView)
//   PATCH /trade/receive-notes/{id}/qc-partial → QCPartial (pending→partial · 返 DetailView)
//   PATCH /trade/receive-notes/{id}/qc-reject  → QCReject  (pending/partial→rejected · body reason · 返 DetailView)
//   PATCH /trade/receive-notes/{id}/cancel     → Cancel    (pending→cancelled · body reason · 返 DetailView)
//
// ★ 动作走 http.patch (同 shipment · gateway WP-05 PATCH state-transition 语义) · 返 ReceiveNoteDetailView (含 Lines)。
// ★ qc-reject / cancel reason 裸标量 {reason:"x"} (v7.5.0 protojson · 空则省略)。
// ★ RBAC (interceptor.go + permission_seed.sql · 全绑 qc 角色): qc-pass/qc-partial → receive_note:qc_pass ·
//   qc-reject → receive_note:qc_reject · cancel → receive_note:cancel。
// ★ qc-pass 副作用 (后端事务内): 写 master.trace_link + 累加 PO line.qty_received (北极星收货闭环数据源)。

export type ListReceiveNotesResponse = ListWrap<ReceiveNoteListItem>;

export interface ListReceiveNotesRequest {
  page?: number;
  size?: number;
  // P2-4 (UX-6): gateway 已解析 ?po_id= (按 PO 反查收货单 · 镜像 shipment so_id) ·
  //   status filter 仍未接线 (留 type 占位)。
  po_id?: number;
  status?: string | string[];
}

/**
 * listReceiveNotes · GET /trade/receive-notes
 * 返 ListWrap<ReceiveNoteListItem> ({items, page}) · 跟 SO/PO/Shipment list 同款 shape。
 * po_id (P2-4): 按 PO 反查收货单 (PO 详情关联单据卡消费) · >0 才发。
 */
export async function listReceiveNotes(
  req: ListReceiveNotesRequest,
): Promise<ListReceiveNotesResponse> {
  const resp = await http.get<ListReceiveNotesResponse>('/api/v1/trade/receive-notes', {
    params: {
      page: req.page,
      size: req.size,
      ...(req.po_id && req.po_id > 0 ? { po_id: req.po_id } : {}),
    },
  });
  return resp.data;
}

/**
 * getReceiveNote · GET /trade/receive-notes/{id}
 * 返 ReceiveNoteDetailView (header 7 字段平铺 + Lines[] · embed 平铺非嵌套 key)。
 */
export async function getReceiveNote(id: string | number): Promise<ReceiveNoteDetailView> {
  const resp = await http.get<ReceiveNoteDetailView>(`/api/v1/trade/receive-notes/${id}`);
  return resp.data;
}

/**
 * qcPassReceiveNote · PATCH /{id}/qc-pass (pending/partial→passed · QC 通过)
 * authorize: receive_note:qc_pass (qc) · guard: all_lines_have_batch_and_coa + all_lines_qty_within_po。
 * 副作用: 写 master.trace_link + 累加 PO line.qty_received。返 ReceiveNoteDetailView。
 */
export async function qcPassReceiveNote(id: string | number): Promise<ReceiveNoteDetailView> {
  const resp = await http.patch<ReceiveNoteDetailView>(
    `/api/v1/trade/receive-notes/${id}/qc-pass`,
    {},
  );
  return resp.data;
}

/**
 * qcPartialReceiveNote · PATCH /{id}/qc-partial (pending→partial · 部分合格)
 * authorize: receive_note:qc_pass (同 qc-pass 权限码) · guard: at_least_one_line_has_batch_and_coa。
 */
export async function qcPartialReceiveNote(id: string | number): Promise<ReceiveNoteDetailView> {
  const resp = await http.patch<ReceiveNoteDetailView>(
    `/api/v1/trade/receive-notes/${id}/qc-partial`,
    {},
  );
  return resp.data;
}

/**
 * qcRejectReceiveNote · PATCH /{id}/qc-reject (pending/partial→rejected · QC 拒绝)
 * authorize: receive_note:qc_reject (qc) · reason 裸标量 {reason:"x"} (空则省略) · 不写 trace_link。
 */
export async function qcRejectReceiveNote(
  id: string | number,
  reason?: string,
): Promise<ReceiveNoteDetailView> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.patch<ReceiveNoteDetailView>(
    `/api/v1/trade/receive-notes/${id}/qc-reject`,
    body,
  );
  return resp.data;
}

/**
 * cancelReceiveNote · PATCH /{id}/cancel (pending→cancelled · 录入有误整单回退)
 * authorize: receive_note:cancel (qc/admin) · reason 裸标量 {reason:"x"} (空则省略)。
 */
export async function cancelReceiveNote(
  id: string | number,
  reason?: string,
): Promise<ReceiveNoteDetailView> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const resp = await http.patch<ReceiveNoteDetailView>(
    `/api/v1/trade/receive-notes/${id}/cancel`,
    body,
  );
  return resp.data;
}

/** CreateReceiveNoteLineInput · 收货行 (proto · Qty {value} · batch/coa Int64Value 可空 · pending 可后补) */
export interface CreateReceiveNoteLineInput {
  line_no: number;
  po_line_id: number;
  qty: WrapperValue; // {value:"50.000"}
  batch_id?: number; // Int64Value 裸 number · 省略 if 0
  coa_report_id?: number; // 省略 if 0
  qc_remark?: string; // StringValue 裸标量
}

/** CreateReceiveNoteRequest · 收货单创建 (proto CreateReceiveNoteRequest · WP-05 v7.7.0 · create-form 收口) */
export interface CreateReceiveNoteRequest {
  rn_no?: string; // 服务端取号（go-live 反馈 #1 · RN{YYMM}{seq:05d}）· 前端不再传 · 创建后详情/列表显示
  po_id: number;
  received_by: number;
  received_at?: string; // Timestamp RFC3339 · 省略 = backend now()
  remark?: string;
  lines: CreateReceiveNoteLineInput[];
}

/**
 * createReceiveNote · POST /trade/receive-notes (201)
 * authorize: receive_note:create (qc) · 返 ReceiveNoteDetailView (含 Lines · 初态 pending)。
 */
export async function createReceiveNote(
  req: CreateReceiveNoteRequest,
): Promise<ReceiveNoteDetailView> {
  const resp = await http.post<ReceiveNoteDetailView>('/api/v1/trade/receive-notes', req);
  return resp.data;
}

// =====================================================
// Aggregate · 按期聚合 (P2a Task 2b · trade v7.12.0 + gateway v7.15.0 · 统计报表页消费)
// =====================================================
// SSOT: apps/oms-gateway/internal/controller/trade_handler.go:898-952 (route 真名 · grep 实证 · 2 endpoint)
//   GET /trade/sales-orders/aggregate    → sales_order:read    (销售额 by order_date)
//   GET /trade/purchase-orders/aggregate → purchase_order:read (采购额 by order_date)
//
// ★ 镜像 finance aggregate 去 basis (SO/PO 无付款流水口径 · booking-only · 见 api/finance.ts 同段):
//   from/to 必填 ISO 日期 [from,to) 半开 · granularity month|year 默认 month。
// ★ 返 AggregateView {Buckets:[{Period,TotalAmount,Count}]} (与 finance 同形 · types/views.ts 共享 type)。

/**
 * aggregateSalesOrders · GET /trade/sales-orders/aggregate (销售订单额按期聚合)
 * authorize: sales_order:read · 真验锚 (lead 实证 2026-06): 月桶 3248.75/2 + 1779.75/4。
 */
export async function aggregateSalesOrders(
  from: string,
  to: string,
  granularity: AggregateGranularity,
): Promise<AggregateView> {
  const resp = await http.get<AggregateView>('/api/v1/trade/sales-orders/aggregate', {
    params: { from, to, granularity },
  });
  return resp.data;
}

/**
 * aggregatePurchaseOrders · GET /trade/purchase-orders/aggregate (采购额按期聚合)
 * authorize: purchase_order:read · 真验锚: 月桶 6215/2 + 1638.5/3。
 */
export async function aggregatePurchaseOrders(
  from: string,
  to: string,
  granularity: AggregateGranularity,
): Promise<AggregateView> {
  const resp = await http.get<AggregateView>('/api/v1/trade/purchase-orders/aggregate', {
    params: { from, to, granularity },
  });
  return resp.data;
}
